<!-- views/CheckoutView.vue -->
<template>
  <div class="checkout-view">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading checkout...</p>
    </div>

    <div v-else-if="course" class="checkout-container">
      <!-- Back Button -->
      <button class="back-btn" @click="$router.back()">← Back to Course</button>

      <div class="checkout-grid">
        <!-- Course Summary -->
        <div class="course-summary">
          <h2>Course Summary</h2>
          <div class="course-card">
            <img
              :src="course.thumbnail || '/default-thumbnail.jpg'"
              :alt="course.title"
            />
            <div class="course-details">
              <h3>{{ course.title }}</h3>
              <p>{{ course.description?.substring(0, 200) }}...</p>
              <div class="course-meta">
                <span>👨‍🏫 {{ course.creatorName }}</span>
                <span>📚 {{ course.totalModules || 0 }} modules</span>
                <span>⏱️ {{ course.totalDuration || "0h 0m" }}</span>
              </div>
            </div>
          </div>

          <div class="price-breakdown">
            <div class="price-row">
              <span>Course Price</span>
              <span>${{ course.price?.toFixed(2) || "0.00" }}</span>
            </div>
            <div v-if="discount" class="price-row discount">
              <span>Discount</span>
              <span>-${{ discount.toFixed(2) }}</span>
            </div>
            <div class="price-row total">
              <span>Total</span>
              <span>${{ total.toFixed(2) }}</span>
            </div>
          </div>

          <div class="payment-methods">
            <h4>Secure Payment</h4>
            <div class="payment-icons">
              <span>💳</span>
              <span>🏦</span>
              <span>📱</span>
            </div>
            <p class="secure-note">🔒 Your payment is secure and encrypted</p>
          </div>
        </div>

        <!-- Payment Form -->
        <div class="payment-form">
          <h2>Payment Details</h2>

          <div class="form-group">
            <label>Card Number</label>
            <div class="card-input">
              <input
                v-model="cardNumber"
                placeholder="1234 5678 9012 3456"
                maxlength="19"
                @input="formatCardNumber"
              />
              <span class="card-type">{{ cardType }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Expiry Date</label>
              <input
                v-model="expiryDate"
                placeholder="MM/YY"
                maxlength="5"
                @input="formatExpiry"
              />
            </div>
            <div class="form-group">
              <label>CVC</label>
              <input
                v-model="cvc"
                placeholder="123"
                maxlength="4"
                type="password"
              />
            </div>
          </div>

          <div class="form-group">
            <label>Name on Card</label>
            <input v-model="cardName" placeholder="John Doe" />
          </div>

          <div class="form-group">
            <label>Email for Receipt</label>
            <input v-model="email" type="email" placeholder="you@example.com" />
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="agreeToTerms" />
              <span
                >I agree to the <a href="#">Terms of Service</a> and
                <a href="#">Refund Policy</a></span
              >
            </label>
          </div>

          <button
            class="btn-primary pay-btn"
            @click="processPayment"
            :disabled="!isFormValid || processing"
          >
            <span v-if="processing" class="spinner-small"></span>
            <span v-else>Pay ${{ total.toFixed(2) }}</span>
          </button>

          <div v-if="error" class="error-message">{{ error }}</div>
        </div>
      </div>
    </div>

    <div v-else class="error-state">
      <svg
        width="80"
        height="80"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <h2>Course Not Found</h2>
      <button class="btn-primary" @click="$router.push('/explore')">
        Browse Courses
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCourseStore } from "@/stores/course";
import api from "@/services/api";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const courseStore = useCourseStore();

const course = ref(null);
const loading = ref(true);
const processing = ref(false);
const error = ref("");

// Payment form
const cardNumber = ref("");
const expiryDate = ref("");
const cvc = ref("");
const cardName = ref("");
const email = ref("");
const agreeToTerms = ref(false);
const discount = ref(0);

const total = computed(() => {
  return (course.value?.price || 0) - discount.value;
});

const cardType = computed(() => {
  const num = cardNumber.value.replace(/\s/g, "");
  if (num.startsWith("4")) return "💳 Visa";
  if (num.startsWith("5")) return "💳 Mastercard";
  if (num.startsWith("3")) return "💳 Amex";
  return "💳 Card";
});

const isFormValid = computed(() => {
  return (
    cardNumber.value.replace(/\s/g, "").length >= 16 &&
    expiryDate.value.length === 5 &&
    cvc.value.length >= 3 &&
    cardName.value.length > 0 &&
    email.value.length > 0 &&
    agreeToTerms.value
  );
});

onMounted(async () => {
  const courseId = route.params.courseId;
  if (!courseId) {
    loading.value = false;
    return;
  }

  const result = await courseStore.fetchCourse(courseId);
  if (result.success) {
    course.value = result.course;
    // Check if already enrolled
    if (course.value.isEnrolled) {
      router.push(`/course/${courseId}`);
    }
  }
  loading.value = false;
});

const formatCardNumber = () => {
  let value = cardNumber.value.replace(/\s/g, "");
  if (value.length > 16) value = value.slice(0, 16);
  cardNumber.value = value.replace(/(\d{4})/g, "$1 ").trim();
};

const formatExpiry = () => {
  let value = expiryDate.value.replace(/\D/g, "");
  if (value.length > 4) value = value.slice(0, 4);
  if (value.length >= 2) {
    expiryDate.value = value.slice(0, 2) + "/" + value.slice(2);
  } else {
    expiryDate.value = value;
  }
};

const processPayment = async () => {
  if (!isFormValid.value) return;

  processing.value = true;
  error.value = "";

  try {
    // Create payment intent on backend
    const response = await api.post("/payments/create-intent", {
      courseId: course.value.id,
      amount: total.value,
      currency: "USD",
    });

    // In a real implementation, you would use Stripe Elements
    // or Stripe.js to handle the payment securely
    // This is a simplified version

    // Simulate payment processing
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Enroll the user
    await api.post(`/enrollments/${course.value.id}`);

    // Redirect to course
    router.push(`/course/${course.value.id}`);
  } catch (err) {
    error.value =
      err.response?.data?.error || "Payment failed. Please try again.";
  } finally {
    processing.value = false;
  }
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.checkout-view {
  margin-top: 72px;
  min-height: 100vh;
  background: #f0f2f5;
  padding: 24px;
}

.checkout-container {
  max-width: 1200px;
  margin: 0 auto;
}

.back-btn {
  background: none;
  border: none;
  color: #495057;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 24px;
  font-size: 16px;

  &:hover {
    color: $primary-orange;
  }
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.course-summary {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h2 {
    margin: 0 0 16px 0;
    color: #1a1a1a;
  }

  .course-card {
    display: flex;
    gap: 16px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 12px;

    img {
      width: 120px;
      height: 80px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .course-details {
      flex: 1;

      h3 {
        margin: 0 0 4px 0;
        color: #1a1a1a;
        font-size: 16px;
      }

      p {
        margin: 0 0 8px 0;
        color: #495057;
        font-size: 14px;
      }

      .course-meta {
        display: flex;
        gap: 12px;
        font-size: 13px;
        color: #868e96;
      }
    }
  }

  .price-breakdown {
    margin: 16px 0;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 12px;

    .price-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;

      &.discount {
        color: #28a745;
      }

      &.total {
        font-weight: 700;
        font-size: 18px;
        border-top: 1px solid #e9ecef;
        margin-top: 8px;
        padding-top: 8px;
        color: #1a1a1a;
      }
    }
  }

  .payment-methods {
    text-align: center;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    h4 {
      margin: 0 0 8px 0;
      color: #868e96;
      font-size: 14px;
    }

    .payment-icons {
      font-size: 32px;
      margin-bottom: 8px;
    }

    .secure-note {
      font-size: 13px;
      color: #868e96;
      margin: 0;
    }
  }
}

.payment-form {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h2 {
    margin: 0 0 16px 0;
    color: #1a1a1a;
  }

  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 6px;
      font-size: 14px;
    }

    input {
      width: 100%;
      padding: 12px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        border-color: $primary-orange;
        outline: none;
      }
    }

    .card-input {
      position: relative;

      .card-type {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 14px;
        color: #868e96;
      }
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 400;

      input {
        width: auto;
      }

      a {
        color: $primary-orange;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .pay-btn {
    width: 100%;
    padding: 14px;
    background: $primary-orange;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover:not(:disabled) {
      background: darken($primary-orange, 10%);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .spinner-small {
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid #fff;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
  }

  .error-message {
    margin-top: 12px;
    padding: 12px;
    background: #fff0f0;
    color: #dc3545;
    border-radius: 8px;
    font-size: 14px;
    border: 1px solid #ffd4d4;
  }
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  text-align: center;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid $primary-orange;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
